/*
 * Copyright 2025 Intuition Robotics ltd
 */
import * as React from "react";
import {useContext, useState} from "react";
import {DialogModule, ToastModule} from "@intuitionrobotics/thunderstorm/frontend";
import {ComposeMessagePanel} from "./ComposeMessagePanel";
import {PatientsModule} from "@modules/PatientsModule";
import {MessagingModule} from "@modules/MessagingModule";
import {PatientSelectionPanel} from "./PatientSelectionPanel";
import {DB_Patient} from "@app/app-shared/patient";
import {AnalyticConsts, AnalyticsModule} from "@modules/AnalyticsModule";
import {UsersModule} from "@modules/UsersModule";
import {GroupType} from "@app/ir-q-app-common/types/contacts";
import {ErrorToast} from "../../../themes/toasts";
import {PatientContextProvider} from "../patients/PatientsContext";
import {messagingRoles} from "@app/app-shared/permission";
import {ChatsContext, ChatsContextType} from "./ChatsContext";
import {CCInbox} from "@app/ir-q-app-common/types/message-api";
import {DB_Message, DB_MessageStatus, MessageMap, MessageStatuses} from "@app/ir-q-app-common/types/message";
import {OrganizationsContext} from "../organizations/OrganizationsContext";
import {flatArray} from "@intuitionrobotics/ts-common";
import {ContactsContext} from "./ContactsContext";

type Props = {
    preselectedPatient?: DB_Patient
}

export const ComposeMessageDialog = (props: Props) => {
    const [selectedPatients, setSelectedPatients] = useState<string[]>([]);
    const [chooseFromList, setChooseFromList] = useState(false);
    const [groupType, setGroupType] = useState<GroupType | undefined>(undefined);
    const cCtx = useContext(ChatsContext);
    const {organizations} = useContext(OrganizationsContext);
    const {contacts} = useContext(ContactsContext);

    const renderComposeMessage = (patients: DB_Patient[], needsGroupSelection: boolean, groupContactIds: string[], availableGroupTypes: Set<GroupType>, preselectedPatient?: DB_Patient) => {
        return <ComposeMessagePanel
            preselectedPatient={preselectedPatient}
            patients={patients}
            selectedGroupType={groupType}
            onSelectGroupType={(_groupType: GroupType) => setGroupType(_groupType)}
            availableGroupTypes={availableGroupTypes}
            selectedPatients={
                preselectedPatient ? [preselectedPatient._id] :
                    selectedPatients
            }
            needsGroupSelection={needsGroupSelection}
            onChooseFromList={() => setChooseFromList(true)}
            onSelectionChange={_selectedPatients => {
                setSelectedPatients([..._selectedPatients])
            }}
            onCancel={() => DialogModule.close()}
            onSend={(dataToSend: { text?: string, files?: File[] }, patientIds, needsResponse: boolean, thumbnailFile?: File, scheduledSend?: number) => {
                if (needsGroupSelection && !groupType)
                    return ErrorToast("You must select a group type.");

                const contactIds: string[] = [];
                patients.forEach(p => {
                    if (patientIds.includes(p._id))
                        contactIds.push(p.contactId);
                })

                const senderGroupContactIds: string[] = groupContactIds.reduce((carry: string[], _id) => {
                    const groupContact = contacts[_id];

                    if (!groupContact)
                        return carry

                    if (groupType && groupContact.contactData.type !== groupType)
                        return carry

                    if (contactIds.some(cId => groupContact.__connectionIds.includes(cId)))
                        carry.push(_id)

                    return carry
                }, []);

                if (!contactIds.length)
                    return;

                const text = dataToSend.text;

                if (scheduledSend) {
                    const logScheduledAnalytics = () => AnalyticsModule.logCareManagerEvent({
                        objectType: AnalyticConsts.ObjectTypes.MESSAGES,
                        entity: AnalyticConsts.Entities.USER,
                        activityStatus: AnalyticConsts.ActivityStatus.SCHEDULE,
                        objectSubType: AnalyticConsts.ObjectSubTypes.COMPOSE_MESSAGE,
                        activityDetails: AnalyticConsts.ActivityDetails.SEND_MESSAGE,
                        frequency: AnalyticConsts.Frequency.SCHEDULED,
                        frequencyValue: scheduledSend
                    });

                    if (text && !dataToSend.files?.length) {
                        MessagingModule.sendTextMessageFromUI(contacts, senderGroupContactIds, contactIds, text, needsResponse, scheduledSend)
                            .then(() => {
                                logScheduledAnalytics();
                                ToastModule.toastSuccess("Message scheduled");
                                DialogModule.close();
                            })
                            .catch(() => ErrorToast("Error scheduling message"));
                    } else if (dataToSend.files?.length) {
                        MessagingModule.sendMediaMessageFromUI(senderGroupContactIds, contactIds, {text, files: dataToSend.files}, needsResponse,
                            async (feId, status) => {
                                const {uploadingDetails, doc} = MessagingModule.onFileStatusChanged(feId, status)
                                if (!doc || !uploadingDetails)
                                    return;

                                await MessagingModule.sendMediaOnBehalf(contacts, uploadingDetails, doc, cCtx,
                                    () => ErrorToast("Error scheduling media message"),
                                    async () => {
                                        logScheduledAnalytics();
                                        ToastModule.toastSuccess("Message scheduled");
                                        DialogModule.close();
                                    }
                                )
                            }, thumbnailFile, scheduledSend).catch(console.error)
                    }
                    return;
                }

                const chats = Object.values(cCtx.chats || {});

                const relevantChats: CCInbox[] = [];
                senderGroupContactIds.forEach(c => {
                    const chatsToUpdate = chats.filter(chat => chat.contactIds.includes(c) && chat.contactIds.some(cId => contactIds.includes(cId)));
                    relevantChats.push(...chatsToUpdate);
                    chatsToUpdate.forEach(chat =>
                        cCtx.setIsSendingToChat(chat._id, true))
                });

                if (text && !dataToSend.files?.length) {
                    MessagingModule.sendTextMessageFromUI(contacts, senderGroupContactIds, contactIds, text, needsResponse)
                        .then(async (_messages) => {
                            const messages = flatArray<DB_Message<any>>(_messages);
                            messages.forEach(m => {
                                AnalyticsModule.logCareManagerEvent({
                                    objectType: AnalyticConsts.ObjectTypes.MESSAGES,
                                    entity: AnalyticConsts.Entities.USER,
                                    activityStatus: AnalyticConsts.ActivityStatus.CREATE,
                                    objectSubType: AnalyticConsts.ObjectSubTypes.COMPOSE_MESSAGE,
                                    activityDetails: AnalyticConsts.ActivityDetails.SEND_MESSAGE,
                                    objectId: m.chatId
                                });

                            });

                            cCtx.saveChats(relevantChats);
                            onSendMessage(true, senderGroupContactIds, {text}, relevantChats, cCtx);
                        })
                        .catch(error => {
                            const relevantChats = chats.filter(chat => chat.contactIds.some(cId => senderGroupContactIds.includes(cId)) && chat.contactIds.some(cId => contactIds.includes(cId)))
                            onSendMessage(false, senderGroupContactIds, {text}, relevantChats, cCtx);
                        })

                } else if (dataToSend.files?.length) {
                    MessagingModule.sendMediaMessageFromUI(senderGroupContactIds, contactIds, {text, files: dataToSend.files}, needsResponse,
                        async (feId, status) => {
                            const {uploadingDetails, doc} = MessagingModule.onFileStatusChanged(feId, status)
                            if (!doc || !uploadingDetails)
                                return;

                            const relevantChats = chats.filter(chat => chat.contactIds.some(cId => senderGroupContactIds.includes(cId)) && chat.contactIds.some(cId => contactIds.includes(cId)))

                            await MessagingModule.sendMediaOnBehalf(contacts, uploadingDetails, doc, cCtx,
                                (data, mimeType, messages, file) => {
                                    onSendMessage(false, senderGroupContactIds, data, relevantChats, cCtx, mimeType, file);
                                },
                                async (data, mimeType, messages, file) => {
                                    messages.forEach(m => {
                                        AnalyticsModule.logCareManagerEvent({
                                            objectType: AnalyticConsts.ObjectTypes.MESSAGES,
                                            entity: AnalyticConsts.Entities.USER,
                                            activityStatus: AnalyticConsts.ActivityStatus.CREATE,
                                            objectSubType: AnalyticConsts.ObjectSubTypes.COMPOSE_MESSAGE,
                                            activityDetails: AnalyticConsts.ActivityDetails.SEND_MESSAGE,
                                            objectId: m.chatId
                                        });
                                    });


                                    cCtx.saveChats(relevantChats);
                                    onSendMessage(true, senderGroupContactIds, data, relevantChats, cCtx, mimeType, file);
                                }
                            )
                        }, thumbnailFile).catch(console.error)
                }
            }}
        />;
    }

    const onSendMessage = (success: boolean, senderGroupContactIds: string[], dataToSend: MessageMap[keyof MessageMap], chats: CCInbox[], {
                               saveMessages,
                               messages,
                               saveChats,
                               setIsSendingToChat
                           }: ChatsContextType,
                           mimeType = "text", file ?: File) => {
        if (!success)
            ErrorToast("Error while sending message");

        for (const senderId of senderGroupContactIds) {
            for (const chat of chats) {

                setIsSendingToChat(chat._id, false);

                const message = MessagingModule.onSentMessageInExistingChat(chat, senderId, dataToSend, mimeType, file);
                const updateStatus: DB_MessageStatus = {...chat.lastMessage.status, status: success ? MessageStatuses.SENT : MessageStatuses.FAILED};
                chat.lastMessage = {...message, status: updateStatus};
                saveChats([chat])

                if (messages?.[chat._id]) {
                    saveMessages([{...message, status: updateStatus}]);
                }
            }
        }

        DialogModule.close();
    }

    const renderSelectFromList = (patients: DB_Patient[]) => {
        return <PatientSelectionPanel
            patients={patients}
            selectedPatients={selectedPatients}
            onCancel={() => {
                setChooseFromList(false);
                setSelectedPatients([]);
            }}
            onOK={() => {
                setChooseFromList(false);
            }}
            onSelectionChange={_selectedPatients => setSelectedPatients(_selectedPatients)}
            okText={"Send"}
        />;
    };

    return <PatientContextProvider roles={messagingRoles}>
        {(pctx) => {
            const needsGroupSelectionAndGroupContactIds = UsersModule.getNeedsGroupSelection(Object.keys(organizations), organizations, props.preselectedPatient);
            if (!needsGroupSelectionAndGroupContactIds)
                return null;

            const needsGroupSelection = needsGroupSelectionAndGroupContactIds.needsGroupSelection || false;
            const {groupContactIds, availableGroupTypes} = needsGroupSelectionAndGroupContactIds;

            if (props.preselectedPatient)
                return renderComposeMessage([props.preselectedPatient], needsGroupSelection, groupContactIds, availableGroupTypes, props.preselectedPatient);

            const patients = PatientsModule.getGroupRecipients(organizations, pctx, contacts, groupType);
            if (!patients)
                return null;

            return chooseFromList ? renderSelectFromList(patients) : renderComposeMessage(patients, needsGroupSelection, groupContactIds, availableGroupTypes);
        }}
    </PatientContextProvider>;

}
